import { useEffect, useState, type ReactNode } from 'react';
import { useAuthStore } from '../../stores/auth';
import { SkeletonCard } from '../../components/Skeleton';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'https://api.profitos.pro';

// =============================================================================
// Types
// =============================================================================

interface SlaTarget {
  id: string;
  serviceName: string;
  endpointPattern: string | null;
  tier: string | null;
  targetP50Ms: number;
  targetP95Ms: number;
  targetP99Ms: number;
  targetAvailability: number;
  targetErrorRate: number;
}

interface ServiceMetrics {
  serviceName: string;
  availability: number;
  avgP95Ms: number;
  totalRequests: number;
}

interface DashboardData {
  overall: {
    availability: number;
    avgP95Ms: number;
    totalRequests24h: number;
    totalErrors24h: number;
    unresolvedIncidents: number;
    incidents7d: number;
  };
  services: ServiceMetrics[];
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

interface ComplianceItem {
  serviceName: string;
  status: 'compliant' | 'breaching' | 'no_data';
  target: SlaTarget;
  actual: {
    p50Ms: number;
    p95Ms: number;
    p99Ms: number;
    availability: number;
    errorRate: number;
    totalRequests: number;
  } | null;
  compliance?: {
    p50: boolean;
    p95: boolean;
    p99: boolean;
    availability: boolean;
    errorRate: boolean;
  };
}

interface ComplianceData {
  compliance: ComplianceItem[];
  summary: {
    total: number;
    compliant: number;
    breaching: number;
    noData: number;
  };
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

interface Incident {
  id: string;
  serviceName: string;
  endpoint: string | null;
  breachType: string;
  targetValue: number;
  actualValue: number;
  startedAt: string;
  resolvedAt: string | null;
  durationMinutes: number | null;
  severity: string;
  notes: string | null;
}

interface IncidentsData {
  incidents: Incident[];
  unresolvedCount: number;
}

// =============================================================================
// API Helpers
// =============================================================================

async function fetchApi<T>(endpoint: string, token: string): Promise<T> {
  const res = await fetch(`${API_URL}${endpoint}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    const data = (await res.json()) as { error?: { message?: string } };
    throw new Error(data.error?.message ?? `Request failed: ${res.status}`);
  }

  const data = (await res.json()) as { success: boolean; data?: T; error?: { message?: string } };
  if (!data.success || !data.data) {
    throw new Error(data.error?.message ?? 'Request failed');
  }

  return data.data;
}

// =============================================================================
// Components
// =============================================================================

function StatusBadge({ status }: { status: string }): ReactNode {
  const styles: Record<string, string> = {
    compliant: 'bg-green-100 text-green-800',
    breaching: 'bg-red-100 text-red-800',
    no_data: 'bg-gray-100 text-gray-800',
    warning: 'bg-yellow-100 text-yellow-800',
    critical: 'bg-red-100 text-red-800',
  };

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${styles[status] ?? styles['no_data']}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

function MetricCard({
  title,
  value,
  unit,
  target,
  isCompliant,
  color,
}: {
  title: string;
  value: number | string;
  unit?: string;
  target?: number;
  isCompliant?: boolean;
  color: 'blue' | 'green' | 'red' | 'yellow' | 'purple';
}): ReactNode {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200',
    green: 'bg-green-50 border-green-200',
    red: 'bg-red-50 border-red-200',
    yellow: 'bg-yellow-50 border-yellow-200',
    purple: 'bg-purple-50 border-purple-200',
  };

  const textColorClasses = {
    blue: 'text-blue-700',
    green: 'text-green-700',
    red: 'text-red-700',
    yellow: 'text-yellow-700',
    purple: 'text-purple-700',
  };

  return (
    <div className={`rounded-lg border p-4 ${colorClasses[color]}`}>
      <p className="text-sm font-medium text-gray-600">{title}</p>
      <p className={`mt-1 text-2xl font-bold ${textColorClasses[color]}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
        {unit && <span className="ml-1 text-sm font-normal">{unit}</span>}
      </p>
      {target !== undefined && (
        <p className={`mt-1 text-xs ${isCompliant === false ? 'text-red-600' : 'text-gray-500'}`}>
          Target: {target}
          {unit}
          {isCompliant !== undefined && (isCompliant ? ' (OK)' : ' (BREACH)')}
        </p>
      )}
    </div>
  );
}

function ServiceCard({ service, target }: { service: ServiceMetrics; target: SlaTarget | undefined }): ReactNode {
  const isAvailabilityOk = target ? service.availability >= target.targetAvailability : true;
  const isLatencyOk = target ? service.avgP95Ms <= target.targetP95Ms : true;
  const isHealthy = isAvailabilityOk && isLatencyOk;

  return (
    <div className={`rounded-lg border p-4 ${isHealthy ? 'border-green-200 bg-white' : 'border-red-200 bg-red-50'}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900">{service.serviceName}</h3>
        <StatusBadge status={isHealthy ? 'compliant' : 'breaching'} />
      </div>
      <div className="grid grid-cols-3 gap-2 text-sm">
        <div>
          <p className="text-gray-500">Availability</p>
          <p className={`font-medium ${isAvailabilityOk ? 'text-green-600' : 'text-red-600'}`}>
            {service.availability.toFixed(2)}%
          </p>
        </div>
        <div>
          <p className="text-gray-500">P95 Latency</p>
          <p className={`font-medium ${isLatencyOk ? 'text-green-600' : 'text-red-600'}`}>
            {service.avgP95Ms}ms
          </p>
        </div>
        <div>
          <p className="text-gray-500">Requests</p>
          <p className="font-medium text-gray-900">{service.totalRequests.toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
}

function IncidentRow({ incident }: { incident: Incident }): ReactNode {
  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleString();
  };

  return (
    <tr className="border-b border-gray-100">
      <td className="py-3 px-4">
        <div className="font-medium text-gray-900">{incident.serviceName}</div>
        {incident.endpoint && (
          <div className="text-sm text-gray-500">{incident.endpoint}</div>
        )}
      </td>
      <td className="py-3 px-4">
        <span className="capitalize">{incident.breachType.replace('_', ' ')}</span>
      </td>
      <td className="py-3 px-4">
        <span className="text-red-600">{incident.actualValue.toFixed(2)}</span>
        <span className="text-gray-400"> / </span>
        <span className="text-gray-600">{incident.targetValue}</span>
      </td>
      <td className="py-3 px-4">
        <StatusBadge status={incident.severity} />
      </td>
      <td className="py-3 px-4 text-sm text-gray-500">
        {formatDate(incident.startedAt)}
      </td>
      <td className="py-3 px-4">
        {incident.resolvedAt ? (
          <span className="text-green-600 text-sm">{incident.durationMinutes}m</span>
        ) : (
          <span className="text-red-600 text-sm">Ongoing</span>
        )}
      </td>
    </tr>
  );
}

// =============================================================================
// Main Page Component
// =============================================================================

export function SlaMonitoringPage(): ReactNode {
  const { accessToken } = useAuthStore();
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [compliance, setCompliance] = useState<ComplianceData | null>(null);
  const [incidents, setIncidents] = useState<IncidentsData | null>(null);
  const [targets, setTargets] = useState<SlaTarget[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'compliance' | 'incidents' | 'targets'>('overview');

  useEffect(() => {
    async function fetchData(): Promise<void> {
      if (!accessToken) return;

      try {
        setLoading(true);
        const [dashboardData, complianceData, incidentsData, targetsData] = await Promise.all([
          fetchApi<DashboardData>('/sla/dashboard', accessToken),
          fetchApi<ComplianceData>('/sla/compliance', accessToken),
          fetchApi<IncidentsData>('/sla/incidents?unresolvedOnly=true&limit=20', accessToken),
          fetchApi<{ targets: SlaTarget[] }>('/sla/targets', accessToken),
        ]);

        setDashboard(dashboardData);
        setCompliance(complianceData);
        setIncidents(incidentsData);
        setTargets(targetsData.targets);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load SLA data');
      } finally {
        setLoading(false);
      }
    }

    void fetchData();
  }, [accessToken]);

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">SLA Monitoring</h1>
        <div className="grid gap-4 md:grid-cols-4">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-red-700">
        <h2 className="font-semibold">Error Loading SLA Data</h2>
        <p className="mt-1">{error}</p>
      </div>
    );
  }

  const getTargetForService = (serviceName: string): SlaTarget | undefined => {
    return targets.find((t) => t.serviceName === serviceName && !t.endpointPattern);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">SLA Monitoring</h1>
        <span className="text-sm text-gray-500">
          Last 24 hours | Updated: {new Date().toLocaleTimeString()}
        </span>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {(['overview', 'compliance', 'incidents', 'targets'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {tab === 'incidents' && incidents && incidents.unresolvedCount > 0 && (
                <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-600">
                  {incidents.unresolvedCount}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && dashboard && (
        <div className="space-y-6">
          {/* Summary Metrics */}
          <div className="grid gap-4 md:grid-cols-4">
            <MetricCard
              title="Overall Availability"
              value={`${dashboard.overall.availability.toFixed(2)}%`}
              color={dashboard.overall.availability >= 99.9 ? 'green' : 'red'}
            />
            <MetricCard
              title="Avg P95 Latency"
              value={dashboard.overall.avgP95Ms}
              unit="ms"
              color={dashboard.overall.avgP95Ms <= 500 ? 'blue' : 'yellow'}
            />
            <MetricCard
              title="Total Requests (24h)"
              value={dashboard.overall.totalRequests24h}
              color="purple"
            />
            <MetricCard
              title="Active Incidents"
              value={dashboard.overall.unresolvedIncidents}
              color={dashboard.overall.unresolvedIncidents > 0 ? 'red' : 'green'}
            />
          </div>

          {/* Service Health */}
          <div className="rounded-lg bg-white p-6 shadow">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Service Health</h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {dashboard.services.map((service) => (
                <ServiceCard
                  key={service.serviceName}
                  service={service}
                  target={getTargetForService(service.serviceName)}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Compliance Tab */}
      {activeTab === 'compliance' && compliance && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="grid gap-4 md:grid-cols-4">
            <MetricCard title="Total Services" value={compliance.summary.total} color="blue" />
            <MetricCard title="Compliant" value={compliance.summary.compliant} color="green" />
            <MetricCard title="Breaching" value={compliance.summary.breaching} color="red" />
            <MetricCard title="No Data" value={compliance.summary.noData} color="yellow" />
          </div>

          {/* Compliance Details */}
          <div className="rounded-lg bg-white shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">P50</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">P95</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">P99</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Availability</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error Rate</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {compliance.compliance.map((item) => (
                  <tr key={item.serviceName}>
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                      {item.serviceName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={item.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {item.actual ? (
                        <span className={item.compliance?.p50 ? 'text-green-600' : 'text-red-600'}>
                          {item.actual.p50Ms}ms
                        </span>
                      ) : '-'}
                      <span className="text-gray-400 text-xs ml-1">/ {item.target.targetP50Ms}ms</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {item.actual ? (
                        <span className={item.compliance?.p95 ? 'text-green-600' : 'text-red-600'}>
                          {item.actual.p95Ms}ms
                        </span>
                      ) : '-'}
                      <span className="text-gray-400 text-xs ml-1">/ {item.target.targetP95Ms}ms</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {item.actual ? (
                        <span className={item.compliance?.p99 ? 'text-green-600' : 'text-red-600'}>
                          {item.actual.p99Ms}ms
                        </span>
                      ) : '-'}
                      <span className="text-gray-400 text-xs ml-1">/ {item.target.targetP99Ms}ms</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {item.actual ? (
                        <span className={item.compliance?.availability ? 'text-green-600' : 'text-red-600'}>
                          {item.actual.availability}%
                        </span>
                      ) : '-'}
                      <span className="text-gray-400 text-xs ml-1">/ {item.target.targetAvailability}%</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {item.actual ? (
                        <span className={item.compliance?.errorRate ? 'text-green-600' : 'text-red-600'}>
                          {item.actual.errorRate}%
                        </span>
                      ) : '-'}
                      <span className="text-gray-400 text-xs ml-1">/ {item.target.targetErrorRate}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Incidents Tab */}
      {activeTab === 'incidents' && incidents && (
        <div className="space-y-6">
          <div className="rounded-lg bg-white shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Active Incidents
                {incidents.unresolvedCount > 0 && (
                  <span className="ml-2 text-sm font-normal text-red-600">
                    ({incidents.unresolvedCount} unresolved)
                  </span>
                )}
              </h2>
            </div>
            {incidents.incidents.length > 0 ? (
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Breach Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual / Target</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Started</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {incidents.incidents.map((incident) => (
                    <IncidentRow key={incident.id} incident={incident} />
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="px-6 py-12 text-center text-gray-500">
                No active incidents. All systems operating within SLA targets.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Targets Tab */}
      {activeTab === 'targets' && (
        <div className="space-y-6">
          <div className="rounded-lg bg-white shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">SLA Targets</h2>
              <p className="mt-1 text-sm text-gray-500">
                Configured performance targets for each service and endpoint
              </p>
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Endpoint</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">P50</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">P95</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">P99</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Availability</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error Rate</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {targets.map((target) => (
                  <tr key={target.id}>
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                      {target.serviceName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                      {target.endpointPattern ?? '(all endpoints)'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{target.targetP50Ms}ms</td>
                    <td className="px-6 py-4 whitespace-nowrap">{target.targetP95Ms}ms</td>
                    <td className="px-6 py-4 whitespace-nowrap">{target.targetP99Ms}ms</td>
                    <td className="px-6 py-4 whitespace-nowrap">{target.targetAvailability}%</td>
                    <td className="px-6 py-4 whitespace-nowrap">{target.targetErrorRate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
